/**
 * Workflow de Retiro EPP
 *
 * Ejecuta el flujo de retiro de EPP ya asignados después de crear el DTE.
 * 1. Espera a que el PDF del DTE esté listo
 * 2. Sube el PDF como archivo (doc_file)
 * 3. Ejecuta el retiro (pick) de los EPPs usando el ID del archivo
 * 
 * REFACTOR: Usa utilidades compartidas de pdfWorkflowUtils para eliminar código duplicado
 */

import {
	deliverWarehouseItem,
	uploadFoldersFile,
	getFileTypeByCode,
} from '../../../../../slices/thunks';
import {
	readDTEFacele,
	createWarehouseGuide,
} from '../../../../../slices/warehouseGuide/thunk';
import { 
	waitForPDF, 
	uploadDocument, 
	generateFileName 
} from '../utils/pdfWorkflowUtils';
import { FOLDER_PATHS } from '../../constants/guideConstants';

export class RetiroWorkflow {
	/**
	 * Ejecuta el workflow de retiro EPP
	 * @param {Function} onProgress - Callback (stepId, status, message?) para reportar progreso
	 */
	static async execute(dispatch, dteResult, formValues, onProgress) {
		const dteId = dteResult?.data?._id;
		const folio = dteResult?.data?.folio || '';
		const rut = dteResult?.data?.dtefacele?.Encabezado?.Emisor?.RUTEmisor || '';

		// Extraer IDs de EPP de los items seleccionados
		const ppeIds = RetiroWorkflow.extractPPEIds(formValues.items);

		if (ppeIds.length === 0) {
			throw new Error(
				'No se encontraron EPPs válidos para retirar. Asegúrese de seleccionar EPPs asignados.'
			);
		}

		// Variables para trackear recursos creados (para rollback)
		let documentId = null;
		let guideId = null;

		try {
			// 1. Esperar a que el PDF del DTE esté listo (usando utilidad compartida)
			if (onProgress) onProgress('wf_pdf', 'loading');

			const pdfBlob = await waitForPDF(dispatch, dteId, readDTEFacele);

			if (!pdfBlob) {
				if (onProgress) onProgress('wf_pdf', 'error', 'No se pudo obtener el PDF');
				const error = new Error(
					'No se pudo obtener el PDF de la guía. El retiro no se puede completar.'
				);
				error.createdResources = { warGuideId: guideId, documentId, folio };
				throw error;
			}
			if (onProgress) onProgress('wf_pdf', 'done');

			// 2. Subir el PDF como archivo (usando utilidad compartida)
			if (onProgress) onProgress('wf_upload', 'loading');
			const fileName = generateFileName(dteResult);
			documentId = await uploadDocument(
				dispatch,
				pdfBlob,
				fileName,
				{
					establishmentId: formValues.establishment_destination_id,
					folderPath: FOLDER_PATHS.EPP_RETIRO,
					getFileTypeByCodeThunk: getFileTypeByCode,
					uploadFoldersFileThunk: uploadFoldersFile,
				}
			);

			if (!documentId) {
				if (onProgress) onProgress('wf_upload', 'error', 'Error al subir documento');
				const error = new Error(
					'No se pudo subir el documento de la guía. El retiro no se puede completar.'
				);
				error.createdResources = { warGuideId: guideId, documentId, folio };
				throw error;
			}
			if (onProgress) onProgress('wf_upload', 'done');

			// 3. Crear registro en war_guide
			if (onProgress) onProgress('wf_guide', 'loading');
			let guideCreationFailed = false;
			try {
				// Extraer warehouse_origin de los items (todos deben venir de la misma bodega)
				const firstItem = formValues.items?.[0];
				const warehouseOriginId = firstItem?._module_vars?.warehouse_id || null;
				const warehouseOriginCode = firstItem?._module_vars?.warehouse_code || null;
				
				const guideData = {
					folio: parseInt(folio),
					dte_id: dteId,
					document_id: documentId,
					establishment_id: formValues.establishment_id,
					guide_category: 'epp',
					operation_type: 'retiro',
					warehouse_origin_id: warehouseOriginId,
					warehouse_destination_id: formValues.warehouse_destination_id || null,
					person_id: formValues.pick_person_id,
					items_payload: {
						personal_protective_equipment_ids: ppeIds,
						items_detail: formValues.items,
					},
					mnt_neto: Math.round(Number(formValues.totals_mnt_neto) || 0),
					mnt_iva: Math.round(Number(formValues.totals_iva) || 0),
					mnt_total: Math.round(Number(formValues.totals_mnt_total) || 0),
					sap_code_origin: warehouseOriginCode,
					sap_code_destination: formValues._warehouse_destination_code || null,
					transfer_id: formValues.transfer_id || null,
					patente: formValues.transfer_patente_id || null,
					driver_rut: formValues.transfer_conductor_rut || null,
					personal_protective_equipment_ids: ppeIds,
					guide_date: new Date().toISOString(),
					observations: formValues.observations || null,
					status_guide_id: 'active',
				};

				const guideResult = await dispatch(
					createWarehouseGuide(guideData)
				).unwrap();
				guideId = guideResult._id;
				if (onProgress) onProgress('wf_guide', 'done');
			} catch (error) {
				console.error('[RetiroWorkflow] Error creando guía:', error);
				guideCreationFailed = true;
				if (onProgress) onProgress('wf_guide', 'warning', 'Guía no registrada, pero retiro continuará');
			}

			// 4. Ejecutar el retiro (pick) de los EPPs
			if (onProgress) onProgress('wf_deliver', 'loading');
			const deliverData = {
				personal_protective_equipment_ids: ppeIds,
				pick: true,
				pick_person_id: formValues.pick_person_id,
				pick_date: new Date().toISOString(),
				pick_document_id: documentId,
				pick_document_number: String(folio),
				pick_guide_id: guideId,
			};

			let deliverResult;
			try {
				deliverResult = await dispatch(
					deliverWarehouseItem(deliverData)
				).unwrap();
				if (onProgress) onProgress('wf_deliver', 'done');
			} catch (deliverError) {
				// Marcar el step como error antes de lanzar
				if (onProgress) onProgress('wf_deliver', 'error', deliverError.message || 'Error al ejecutar retiro');
				const error = new Error(deliverError.message || 'Error al ejecutar el retiro de EPPs');
				error.createdResources = { warGuideId: guideId, documentId, folio };
				throw error;
			}

			return {
				success: true,
				ppeIds,
				documentId,
				deliverResult,
				guideId,
				guideCreationFailed,
				warningMessage: guideCreationFailed 
					? `Guía no registrada. Folio: ${folio}, Documento: ${documentId}`
					: null,
				createdResources: {
					warGuideId: guideId,
					documentId: documentId,
					folio: folio
				}
			};
		} catch (error) {
			// Asegurar que el error incluya recursos creados para rollback
			if (!error.createdResources) {
				error.createdResources = { warGuideId: guideId, documentId, folio };
			}
			throw error;
		}
	}

	/**
	 * Extrae los IDs únicos de EPP de los items del formulario
	 * NOTA: Este método es específico de este workflow, no está en utilidades compartidas
	 */
	static extractPPEIds(items) {
		if (!Array.isArray(items)) return [];

		const allIds = [];

		items.forEach(item => {
			const moduleVars = item._module_vars || {};

			if (Array.isArray(moduleVars.personal_protective_equipment_ids)) {
				allIds.push(...moduleVars.personal_protective_equipment_ids);
			} else if (moduleVars.personal_protective_equipment_id) {
				allIds.push(moduleVars.personal_protective_equipment_id);
			}
		});

		return [...new Set(allIds.filter(Boolean))];
	}
}
